import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Sparkles, Send, Globe, Facebook, Instagram } from 'lucide-react';

import { authHeaders } from '../lib/authToken';
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function AdminIA() {
  const [products, setProducts] = useState([]);
  const [enriching, setEnriching] = useState(null);
  const [pubForm, setPubForm] = useState({ platform: 'facebook', sujet: '', produits: '' });
  const [pubResult, setPubResult] = useState(null);
  const [pubLoading, setPubLoading] = useState(false);
  const [articleForm, setArticleForm] = useState({ sujet: '', localite: 'Figeac' });
  const [articleResult, setArticleResult] = useState(null);
  const [articleLoading, setArticleLoading] = useState(false);

  useEffect(() => {
    fetch(`${BACKEND}/api/products?limit=20`).then(r => r.json()).then(d => setProducts(d.products || [])).catch((err) => { /* eslint-disable-line no-empty */ });
  }, []);

  async function enrichProduct(id) {
    setEnriching(id);
    try {
      const res = await fetch(`${BACKEND}/api/ia/enrichir-produit/${id}`, { method: 'POST', headers: authHeaders() });
      const data = await res.json();
      if (data.success) toast.success('✨ Produit enrichi par IA !');
      else toast.error('Erreur IA');
    } catch (e) { toast.error('Erreur'); } finally { setEnriching(null); }
  }

  async function generatePublication() {
    setPubLoading(true);
    try {
      const res = await fetch(`${BACKEND}/api/ia/publication`, {
        method: 'POST', headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ platform: pubForm.platform, sujet: pubForm.sujet, produits: pubForm.produits ? pubForm.produits.split(',').map(p => p.trim()) : [] })
      });
      const data = await res.json();
      setPubResult(data);
    } catch (e) { toast.error('Erreur IA'); } finally { setPubLoading(false); }
  }

  async function generateArticle() {
    setArticleLoading(true);
    try {
      const res = await fetch(`${BACKEND}/api/ia/article-seo`, {
        method: 'POST', headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(articleForm)
      });
      const data = await res.json();
      setArticleResult(data);
    } catch (e) { toast.error('Erreur IA'); } finally { setArticleLoading(false); }
  }

  async function publishArticle() {
    if (!articleResult) return;
    const res = await fetch(`${BACKEND}/api/blog`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...articleResult, titre: articleResult.titre, publie: false })
    });
    if (res.ok) toast.success('Article créé en brouillon !');
  }

  const PLATFORMS = [
    { id: 'facebook', label: 'Facebook', icon: <Facebook size={16} />, color: '#C5D8F9' },
    { id: 'instagram', label: 'Instagram', icon: <Instagram size={16} />, color: '#F9C5D1' },
    { id: 'google', label: 'Google', icon: <Globe size={16} />, color: '#3DDC97' },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      <div style={{ marginBottom: 20 }}>
        <p className="adm-eyebrow">Parametres</p>
        <h2 style={{ fontFamily: 'var(--admin-font-display)', fontSize: 26, fontWeight: 700,
          letterSpacing: '-.6px', color: 'var(--admin-text)', marginTop: 4 }}>IA Pilotage</h2>
        <p style={{ color: 'var(--admin-muted)', fontSize: 13.5, marginTop: 3 }}>Generation de contenu par IA</p>
      </div>

      <div><h2 className="font-display text-xl font-bold" style={{ color: 'var(--admin-text)' }}>IA & Publications</h2>
        <p className="text-xs" style={{ color: 'var(--admin-muted)' }}>Enrichissement IA, publication locale, génération SEO</p></div>

      {/* Enrichissement catalogue */}
      <div className="p-5 rounded-xl" style={{ background: 'var(--admin-surface)', border: '1px solid #2A2A2A' }}>
        <h3 className="font-semibold text-sm mb-4 flex items-center gap-2" style={{ color: 'var(--admin-gold)' }}><Sparkles size={16} /> Enrichissement catalogue</h3>
        <p className="text-xs mb-4" style={{ color: 'var(--admin-muted)' }}>Cliquez sur un produit pour générer automatiquement : description, tags SEO, FAQ et suggestions upsell.</p>
        <div className="space-y-2">
          {products.slice(0, 8).map(p => (
            <div key={p.id} className="flex items-center justify-between gap-3 px-4 py-2.5 rounded-xl" style={{ background: '#1C1C1C' }}>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate" style={{ color: 'var(--admin-text)' }}>{p.nom}</p>
              </div>
              <div className="flex items-center gap-2">
                {p.tags?.length > 0 && <span className="text-xs" style={{ color: '#3DDC97' }}>✓ Enrichi</span>}
                <button onClick={() => enrichProduct(p.id)} disabled={enriching === p.id}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1"
                  style={{ background: 'rgba(212,175,55,0.12)', color: 'var(--admin-gold)', border: '1px solid rgba(212,175,55,0.2)' }}>
                  {enriching === p.id ? <div className="w-3 h-3 rounded-full animate-spin" style={{ border: '1.5px solid #D4AF37', borderTopColor: 'transparent' }} /> : <Sparkles size={12} />}
                  {enriching === p.id ? 'IA...' : 'Enrichir'}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Publication locale */}
      <div className="p-5 rounded-xl" style={{ background: 'var(--admin-surface)', border: '1px solid #2A2A2A' }}>
        <h3 className="font-semibold text-sm mb-4 flex items-center gap-2" style={{ color: 'var(--admin-gold)' }}><Send size={16} /> Publication locale</h3>
        <div className="space-y-3 mb-4">
          <div className="flex gap-2">
            {PLATFORMS.map(p => (
              <button key={p.id} onClick={() => setPubForm(f => ({ ...f, platform: p.id }))}
                className="flex-1 flex items-center gap-2 justify-center py-2 rounded-xl text-xs font-medium"
                style={{ background: pubForm.platform === p.id ? `${p.color}15` : '#1C1C1C', border: pubForm.platform === p.id ? `1px solid ${p.color}50` : '1px solid #2A2A2A', color: pubForm.platform === p.id ? p.color : 'rgba(255,255,255,0.4)' }}>
                {p.icon} {p.label}
              </button>
            ))}
          </div>
          <div>
            <label className="block text-xs mb-1" style={{ color: 'var(--admin-muted)' }}>Sujet du post</label>
            <input type="text" value={pubForm.sujet} onChange={e => setPubForm(f => ({ ...f, sujet: e.target.value }))}
              placeholder="Ex: Offre anniversaire enfant week-end du 15 juin"
              className="w-full px-3 py-2.5 rounded-xl text-sm" style={{ background: '#1C1C1C', border: '1px solid #2A2A2A', color: 'var(--admin-text)' }} />
          </div>
          <div>
            <label className="block text-xs mb-1" style={{ color: 'var(--admin-muted)' }}>Produits mis en avant (séparés par virgule)</label>
            <input type="text" value={pubForm.produits} onChange={e => setPubForm(f => ({ ...f, produits: e.target.value }))}
              placeholder="Château gonflable, barbe à papa"
              className="w-full px-3 py-2.5 rounded-xl text-sm" style={{ background: '#1C1C1C', border: '1px solid #2A2A2A', color: 'var(--admin-text)' }} />
          </div>
          <button onClick={generatePublication} disabled={pubLoading || !pubForm.sujet}
            className="btn-gold w-full py-2.5 rounded-xl text-sm font-semibold disabled:opacity-40 flex items-center gap-2 justify-center">
            {pubLoading ? <div className="w-4 h-4 rounded-full animate-spin" style={{ border: '2px solid #0D0D0D', borderTopColor: 'rgba(0,0,0,0.3)' }} /> : <Sparkles size={14} />}
            Générer le post
          </button>
        </div>
        {pubResult && (
          <div className="p-4 rounded-xl" style={{ background: '#1C1C1C', border: '1px solid rgba(212,175,55,0.2)' }}>
            <p className="text-xs font-semibold mb-2" style={{ color: 'var(--admin-gold)' }}>Post généré pour {pubResult.platform}</p>
            <p className="text-sm whitespace-pre-line mb-3" style={{ color: 'var(--admin-text)', lineHeight: '1.6' }}>{pubResult.texte}</p>
            <div className="flex flex-wrap gap-1">
              {pubResult.hashtags?.map((h, i) => <span key={`col-${i}`} className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'rgba(197,216,249,0.1)', color: '#C5D8F9' }}>{h}</span>)}
            </div>
            <p className="text-xs mt-3" style={{ color: 'var(--admin-gold)' }}>CTA : {pubResult.cta}</p>
            <button onClick={() => { navigator.clipboard.writeText(pubResult.texte); toast.success('Copié !'); }} className="mt-3 px-4 py-1.5 rounded-lg text-xs" style={{ background: '#2A2A2A', color: 'rgba(255,255,255,0.6)' }}>Copier le texte</button>
          </div>
        )}
      </div>

      {/* Article SEO */}
      <div className="p-5 rounded-xl" style={{ background: 'var(--admin-surface)', border: '1px solid #2A2A2A' }}>
        <h3 className="font-semibold text-sm mb-4" style={{ color: 'var(--admin-gold)' }}>Article SEO local</h3>
        <div className="grid sm:grid-cols-2 gap-3 mb-3">
          <div>
            <label className="block text-xs mb-1" style={{ color: 'var(--admin-muted)' }}>Sujet</label>
            <input type="text" value={articleForm.sujet} onChange={e => setArticleForm(f => ({ ...f, sujet: e.target.value }))}
              placeholder="Organiser un anniversaire enfant"
              className="w-full px-3 py-2.5 rounded-xl text-sm" style={{ background: '#1C1C1C', border: '1px solid #2A2A2A', color: 'var(--admin-text)' }} />
          </div>
          <div>
            <label className="block text-xs mb-1" style={{ color: 'var(--admin-muted)' }}>Localité</label>
            <input type="text" value={articleForm.localite} onChange={e => setArticleForm(f => ({ ...f, localite: e.target.value }))}
              className="w-full px-3 py-2.5 rounded-xl text-sm" style={{ background: '#1C1C1C', border: '1px solid #2A2A2A', color: 'var(--admin-text)' }} />
          </div>
        </div>
        <button onClick={generateArticle} disabled={articleLoading || !articleForm.sujet}
          className="btn-gold px-5 py-2.5 rounded-xl text-sm font-semibold disabled:opacity-40">
          {articleLoading ? 'Génération IA...' : 'Générer l\'article'}
        </button>
        {articleResult && (
          <div className="mt-4 p-4 rounded-xl" style={{ background: '#1C1C1C', border: '1px solid rgba(212,175,55,0.2)' }}>
            <h4 className="font-semibold mb-2" style={{ color: 'var(--admin-text)' }}>{articleResult.titre}</h4>
            <p className="text-xs mb-2" style={{ color: 'var(--admin-muted)' }}>{articleResult.extrait}</p>
            <div className="flex flex-wrap gap-1 mb-3">
              {articleResult.tags?.map((t, i) => <span key={`col-${i}`} className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'rgba(197,216,249,0.1)', color: '#C5D8F9' }}>{t}</span>)}
            </div>
            <button onClick={publishArticle} className="btn-gold px-4 py-2 rounded-xl text-xs font-semibold">Enregistrer en brouillon</button>
          </div>
        )}
      </div>
    </div>
  );
}
